import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearSelectedEvent,
  fetchCategoryPricingStats,
  fetchEventFiscalSerials,
  fetchEventHeavy,
  fetchStatistics,
} from '../redux/slices/eventsSlice';
import { fetchNotifications } from '../redux/slices/notificationsSlice';
import { clearReservations, fetchReservations, setFilters } from '../redux/slices/reservationsSlice';
import { clearOrders, fetchOrders, setOrdersFilter } from '../redux/slices/ordersSlice';
import { downloadFile, openFileInNewTab } from '../utils/fileUtils';
import { seatIdToSectionParentOwn } from '../utils/formatters';
import { eventUrl } from '../utils/urlUtils';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CalendarIcon from '@mui/icons-material/CalendarToday';
import LocationIcon from '@mui/icons-material/LocationOn';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import InfoIcon from '@mui/icons-material/Info';
import SearchOutlined from '@mui/icons-material/SearchOutlined';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Chip from '@mui/material/Chip';
import Download from '@mui/icons-material/Download';
import TabPanel from '../components/TabPanel';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';
import CategoryConfigDialog from '../components/CategoryConfigDialog';
import ForSaleConfigDialog from '../components/ForSaleConfigDialog';
import EventsDrawer, { EventsDrawerType } from '../components/EventsDrawer';
import SalesChart from '../components/SalesChart';
import NoData from '../components/NoData';
import CategoryPricingStats from '../components/CategoryPricingStats';
import ReservationsDialog from '../components/reservations/ReservationsDialog';
import OrdersDialog from '../components/orders/OrdersDialog';
import NotificationsDialog from '../components/NotificationsDialog';
import EventHistoryDialog from '../components/EventHistoryDialog';
import TicketPdfSelectorDialog from '../components/TicketPDFSelectorDialog';
import ReservationsTable from '../components/reservations/ReservationsTable';
import TicketsTable from '../components/TicketsTable';
import api from '../api';

function EventDetails() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { eventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabIndex, setTabIndex] = useState(parseInt(searchParams.get('tabIndex')) || 0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openCatConfig, setOpenCatConfig] = useState(false);

  const [openForSaleConfig, setOpenForSaleConfig] = useState(false);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [openReservations, setOpenReservations] = useState(false);
  const [openOrders, setOpenOrders] = useState(false);
  const [openHistory, setOpenHistory] = useState(false);
  const [selectedOrdId, setSelectedOrdId] = useState(null);
  const [editEvent, setEditEvent] = useState(null);

  const event = useSelector(({ events: { selectedEvent } }) => selectedEvent);
  const isLoading = useSelector(({ events: { isLoading } }) => isLoading);
  const error = useSelector(({ events: { error } }) => error);

  const fiscalSerials = useSelector(({ events: { fiscalSerials } }) => fiscalSerials);
  const notifications = useSelector((state) => state.notifications.data);
  const categoryPricingStats = useSelector((state) => state.events.categoryPricingStats);
  const isLoadingCategoryPricings = useSelector((state) => state.events.isLoadingCategoryPricings);
  const saleStatistics = useSelector((state) => state.events.saleStatistics);

  const reservations = useSelector((state) => state.reservations.data);
  const reservationError = useSelector((state) => state.reservations.error);
  const totalItems = useSelector((state) => state.reservations.totalItems);
  const options = useSelector((state) => state.reservations.options);

  const tickets = useSelector((state) => state.orders.data);
  const totalItemsTickets = useSelector((state) => state.orders.totalItems);
  const optionsTickets = useSelector((state) => state.orders.options);

  const user = useSelector((state) => state.auth.user);

  const handleChangePage = (_, newPage) => {
    dispatch(setFilters({ page: newPage + 1 }));
    dispatch(fetchReservations({ eventId, page: newPage + 1, limit: options.limit, search: options.search || '' }));
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    dispatch(setFilters({ limit: newLimit, page: 1 }));
    dispatch(fetchReservations({ eventId, page: 1, limit: newLimit, search: options.search || '' }));
  };

  const handleOnSearchClick = () => {
    dispatch(setFilters({ page: 1, search: options.search || '' }));
    dispatch(fetchReservations({ eventId, page: options.page, limit: options.limit, search: options.search || '' }));
  };

  const handleChangePageTickets = (_, newPage) => {
    dispatch(setOrdersFilter({ page: newPage + 1 }));
    dispatch(
      fetchOrders({
        eventId,
        page: newPage + 1,
        limit: optionsTickets.limit,
        search: optionsTickets.search || '',
      })
    );
  };

  const handleChangeRowsPerPageTickets = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    dispatch(setOrdersFilter({ limit: newLimit, page: 1 }));
    dispatch(
      fetchOrders({
        eventId,
        page: 1,
        limit: newLimit,
        search: optionsTickets.search || '',
      })
    );
  };

  const handleOnSearchClickTickets = () => {
    dispatch(setOrdersFilter({ page: 1, search: optionsTickets.search || '' }));
    dispatch(
      fetchOrders({
        eventId,
        page: optionsTickets.page,
        limit: optionsTickets.limit,
        search: optionsTickets.search || '',
      })
    );
  };

  const handleIndexChange = (newValue) => {
    setTabIndex(newValue);
    if (newValue === 2) {
      dispatch(fetchReservations({ eventId, ...options }));
    }
    if (newValue === 1) {
      dispatch(fetchOrders({ eventId, ...optionsTickets }));
    }
  };

  useEffect(() => {
    if (error || reservationError) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [error, reservationError, t]);

  const copyScanUrl = useCallback(() => {
    const code = event.scan_code;
    navigator.clipboard
      .writeText(`${process.env.REACT_APP_PUBLIC_URL}/scan/${code}`)
      .then(() =>
        toast.success(t('Copied to clipboard'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        })
      )
      .catch(() =>
        toast.error(t('An error occured, try again!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        })
      );
    handleClose();
  }, [event, t]);

  const openDocument = (documentType) => {
    return () => {
      const tabWindow = window.open('', '_blank');
      handleClose();
      api
        .get(`/events/${eventId}/fiscalDocuments?type=${documentType}`, { responseType: 'blob' })
        .then((res) => {
          const data = res.data;
          openFileInNewTab(tabWindow, data, 'application/pdf');
        })
        .catch((err) => {
          toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
        });
    };
  };

  const downloadOrdersCsv = (eventId) => {
    api
      .get(`/events/${eventId}/ordersCsv`, { responseType: 'blob' })
      .then((res) => {
        const data = res.data;
        downloadFile(data, 'text/csv', `orders_event_${eventId}.csv`);
      })
      .catch((err) => {
        toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
      });
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleOpenEdit = useCallback(() => {
    setEditEvent(event);
    handleClose();
  }, [event]);

  const toggleOpenNotifications = useCallback(() => {
    setOpenNotifications(!openNotifications);
    handleClose();
  }, [openNotifications]);

  const toggleOpenReservations = useCallback(() => {
    setOpenReservations(!openReservations);
  }, [openReservations]);

  const toggleOpenOrders = useCallback(() => {
    setOpenOrders(!openOrders);
  }, [openOrders]);

  const toggleOpenCatConfig = () => {
    setOpenCatConfig(!openCatConfig);
    handleClose();
  };

  const toggleOpenForSaleConfig = () => {
    setOpenForSaleConfig(!openForSaleConfig);
    handleClose();
  };

  const toggleHistory = () => {
    setOpenHistory(!openHistory);
    handleClose();
  };

  const toClient = () => {
    window.open(eventUrl(event));
    handleClose();
  };

  const onDrawerClose = () => {
    setEditEvent(null);
  };

  const statusChip = useMemo(() => {
    const { status } = event;

    if (!user || !status) return null;

    const chipLabel = status.replace('_', ' ');
    const chipColor =
      status === 'NOT_SELLING' || status === 'DELETED' ? 'error' : status === 'SELLING' ? 'success' : 'warning';
    const clickAction = user.role === 'organizer' ? { onClick: toggleOpenEdit } : {};

    return (
      <Chip
        {...clickAction}
        sx={{ mt: 1, borderRadius: 1 }}
        size="small"
        label={t(`${chipLabel}`)}
        variant="outlined"
        color={chipColor}
      />
    );
  }, [event, user, toggleOpenEdit, t]);

  useEffect(() => {
    if (eventId) {
      dispatch(fetchEventHeavy({ eventId }));
      dispatch(fetchCategoryPricingStats({ eventId }));
      dispatch(fetchStatistics({ eventId }));
      if (user.role === 'organizer') {
        dispatch(fetchEventFiscalSerials({ eventId }));
        dispatch(fetchNotifications({ eventId }));
      }
    }

    return () => {
      dispatch(clearSelectedEvent());
      dispatch(clearReservations());
      dispatch(clearOrders());
    };
  }, [dispatch, eventId, user.role]);

  useEffect(() => {
    const filters = {
      eventId,
      page: Number(searchParams.get('page')) || 1,
      limit: Number(searchParams.get('limit')) || 10,
      search: searchParams.get('search') || '',
    };

    if (tabIndex === 2) {
      dispatch(setFilters(filters));
      dispatch(fetchReservations(filters));
    }
    if (tabIndex === 1) {
      dispatch(setOrdersFilter(filters));
      dispatch(fetchOrders(filters));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tabIndex === 2) {
      setSearchParams(
        {
          tabIndex,
          page: options.page,
          limit: options.limit,
          search: options.search || '',
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex, options.page, options.limit, options.search]);

  useEffect(() => {
    if (tabIndex === 3) {
      dispatch(
        fetchOrders({
          eventId,
          page: 1,
          limit: 0,
          search: '',
        })
      );
    }
  }, [dispatch, eventId, tabIndex]);

  useEffect(() => {
    if (tabIndex === 1) {
      setSearchParams(
        {
          tabIndex,
          page: optionsTickets.page,
          limit: optionsTickets.limit,
          search: optionsTickets.search || '',
        },
        { replace: true }
      );
    } else if (tabIndex !== 2) {
      setSearchParams({ tabIndex }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIndex, optionsTickets.page, optionsTickets.limit, optionsTickets.search]);

  const subtotalOnline = useMemo(
    () => categoryPricingStats.revenue - categoryPricingStats.comission,
    [categoryPricingStats]
  );

  const subtotalCashier = useMemo(
    () => categoryPricingStats.revenue_cashier - categoryPricingStats.comission_cashier,
    [categoryPricingStats]
  );

  const total = useMemo(() => subtotalOnline + subtotalCashier, [subtotalOnline, subtotalCashier]);
  const searchHelperText = t(
    'Search using reservation id, reservation ref, order id, event id, name, email, phone or notes'
  );
  const searchHelperTextTickets = t('searchHelperTextTickets');
  const totalBooked = useMemo(
    () => categoryPricingStats.booked + categoryPricingStats.booked_cashier + categoryPricingStats.booked_organizer,
    [categoryPricingStats]
  );
  const fiscalDocumentsDisabled = !!event.is_fiscal_serial_unique || Number(event.has_event_date) === 0;

  return (
    <div className="event-details">
      <Box sx={{ position: 'relative' }}>
        <IconButton
          aria-label="more options"
          onClick={handleMenu}
          sx={{
            position: 'absolute',
            right: 8,
          }}
        >
          <MoreHorizIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={!!anchorEl}
          onClose={handleClose}
        >
          <Box>
            {user.role === 'cashier' && (
              <Box>
                <MenuItem onClick={toClient}>{t('To client')}</MenuItem>
                <MenuItem onClick={toggleOpenForSaleConfig}>{t('For Sale Config')}</MenuItem>
              </Box>
            )}
            {user.role === 'organizer' && (
              <Box>
                <MenuItem onClick={toClient}>{t('To client')}</MenuItem>
                <MenuItem onClick={toggleOpenEdit}>{t('Edit')}</MenuItem>
                <MenuItem onClick={toggleHistory}>{t('History')}</MenuItem>
                <MenuItem onClick={copyScanUrl}>{t('Copy scan link')}</MenuItem>
                <Divider />
                <MenuItem onClick={toggleOpenCatConfig}>{t('Category Config')}</MenuItem>
                <MenuItem onClick={toggleOpenForSaleConfig}>{t('For Sale Config')}</MenuItem>
                <Divider />
                <MenuItem disabled={fiscalDocumentsDisabled} onClick={openDocument('fiscalRegistration')}>
                  {t('Registration')}
                </MenuItem>
                <MenuItem disabled={fiscalDocumentsDisabled} onClick={openDocument('destroyedTickets')}>
                  {t('Destroyed Tickets')}
                </MenuItem>
                <MenuItem disabled={fiscalDocumentsDisabled} onClick={openDocument('soldTickets')}>
                  {t('Sold Tickets')}
                </MenuItem>
              </Box>
            )}
          </Box>
        </Menu>
        <Grid wrap="nowrap" container>
          <Grid item>
            {!event.portrait_image_url ? (
              <Skeleton variant="rectangular" width={200} height={280} />
            ) : (
              <img alt="" src={event.portrait_image_url} width="200px" height="100%" />
            )}
          </Grid>
          <Grid item>
            <Box sx={{ p: 2 }}>
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  toggleOpenOrders();
                }}
                sx={{ color: 'white', cursor: 'pointer', mb: 2 }}
                underline="hover"
                variant="h4"
              >
                {event.title}
              </Link>
              <Box mb={2} />
              <Box display="flex" alignItems="center">
                <InfoIcon sx={{ mr: 1 }} fontSize="small" />
                <Typography variant="subtitle1">{t(event.type)}</Typography>
              </Box>
              {user.role !== 'organizer' && (
                <Box display="flex" alignItems="center">
                  <GroupsIcon sx={{ mr: 1 }} fontSize="small" />
                  <Typography variant="subtitle1">{event.organizer}</Typography>
                </Box>
              )}
              {event.has_event_date !== 0 && (
                <Box display="flex" alignItems="center">
                  <CalendarIcon sx={{ mr: 1 }} fontSize="small" />
                  <Typography variant="subtitle1">
                    {dayjs(event.date).format('DD/MM/YYYY')} {dayjs(event.date).format('HH:mm')}
                  </Typography>
                </Box>
              )}
              <Box display="flex" alignItems="center">
                <LocationIcon sx={{ mr: 1 }} fontSize="small" />
                <Typography variant="subtitle1">{`${event.location}`}</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <LocationCityIcon sx={{ mr: 1 }} fontSize="small" />
                <Typography variant="subtitle1">{`${event.city}`}</Typography>
              </Box>
              {statusChip}
              {!!event.is_one_plus_one_free && (
                <Chip
                  sx={{ ml: 1, mt: 1, borderRadius: 1 }}
                  size="small"
                  color="success"
                  variant="outlined"
                  label={t('1+1 Free')}
                />
              )}
              {!!event.is_three_plus_one_free && (
                <Chip
                  sx={{ ml: 1, mt: 1, borderRadius: 1 }}
                  size="small"
                  color="success"
                  variant="outlined"
                  label={t('3+1 Free')}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabIndex} onChange={(_, newValue) => handleIndexChange(newValue)}>
          <Tab label={t('Details')} />
          <Tab label={t('Tickets')} />
          <Tab label={t('Reservations')} />
          {user.role === 'organizer' && <Tab label={t('Notifications')} />}
          {user.role === 'organizer' && <Tab label={t('DGITL')} />}
        </Tabs>
      </Box>
      <TabPanel disabled={isLoading} value={tabIndex} index={0}>
        <SalesChart saleStatistics={saleStatistics} />
        <CategoryPricingStats
          eventId={eventId}
          role={user.role}
          stats={categoryPricingStats}
          loading={isLoadingCategoryPricings}
        />
        {user.role === 'organizer' && (
          <TableContainer sx={{ marginTop: 2, width: '35%', marginLeft: 'auto' }} component={Box}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell colSpan={2}>{t('Summary')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow
                  sx={{
                    textAlign: 'right',
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                  }}
                >
                  <TableCell>{t('Seats booked')}</TableCell>
                  <TableCell align="right">{totalBooked}</TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    textAlign: 'right',
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                  }}
                >
                  <TableCell>{t('Online revenue')}</TableCell>
                  <TableCell align="right">
                    {categoryPricingStats.revenue.toFixed(2)}
                    {t('Lei')}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    textAlign: 'right',
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                  }}
                >
                  <TableCell>{t('Online comission')}</TableCell>
                  <TableCell align="right">
                    {categoryPricingStats.comission.toFixed(2)}
                    {t('Lei')}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    textAlign: 'right',
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('Online subtotal')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {subtotalOnline.toFixed(2)}
                    {t('Lei')}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    textAlign: 'right',
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                  }}
                >
                  <TableCell>{t('Cashier revenue')}</TableCell>
                  <TableCell align="right">
                    {categoryPricingStats.revenue_cashier.toFixed(2)}
                    {t('Lei')}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    textAlign: 'right',
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                  }}
                >
                  <TableCell>{t(`Cashier comission`)}</TableCell>
                  <TableCell align="right">
                    {categoryPricingStats.comission_cashier.toFixed(2)}
                    {t('Lei')}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    textAlign: 'right',
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('Cashier subtotal')}</TableCell>
                  <TableCell align="right">
                    {subtotalCashier.toFixed(2)}
                    {t('Lei')}
                  </TableCell>
                </TableRow>
                <TableRow
                  sx={{
                    textAlign: 'right',
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                  }}
                >
                  <TableCell sx={{ fontWeight: 'bold' }}>{t('Total')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    {total.toFixed(2)}
                    {t('Lei')}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>
      <TabPanel disabled={isLoading} value={tabIndex} index={1}>
        <Paper sx={{ p: 2, mb: 1 }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5">{t('Tickets')}</Typography>
            </Grid>

            <Grid item>
              <Grid container spacing={2} alignItems="center">
                <Grid item sx={{ width: 350 }}>
                  <TextField
                    onChange={(event) => dispatch(setOrdersFilter({ search: event.target.value }))}
                    value={optionsTickets.search}
                    sx={{ marginRight: 4 }}
                    inputProps={{ autoComplete: 'off' }}
                    label={t('Search')}
                    fullWidth
                    placeholder={searchHelperTextTickets}
                    variant="outlined"
                    type="text"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={handleOnSearchClickTickets} edge="end">
                          <SearchOutlined />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item>
                  <IconButton onClick={() => downloadOrdersCsv(eventId)} color="primary" size="medium">
                    <Download />
                  </IconButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
        <TicketsTable
          user={user.role}
          tickets={tickets}
          setSelectedOrdId={setSelectedOrdId}
          seatIdToSectionParentOwn={seatIdToSectionParentOwn}
          pagination={{
            totalItems: totalItemsTickets,
            page: optionsTickets.page,
            limit: optionsTickets.limit,
            onPageChange: handleChangePageTickets,
            onRowsPerPageChange: handleChangeRowsPerPageTickets,
          }}
        />
      </TabPanel>
      <TabPanel disabled={isLoading} value={tabIndex} index={2}>
        <Paper sx={{ p: 2, mb: 1 }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5">{t('Reservations')}</Typography>
            </Grid>
            <Grid item>
              <Grid container spacing={2} alignItems="center">
                <Grid item sx={{ width: 350 }}>
                  <TextField
                    onChange={(event) => dispatch(setFilters({ search: event.target.value }))}
                    value={options.search}
                    sx={{ marginRight: 4 }}
                    inputProps={{ autoComplete: 'off' }}
                    label={t('Search')}
                    fullWidth
                    placeholder={searchHelperText}
                    variant="outlined"
                    type="text"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={handleOnSearchClick} edge="end">
                          <SearchOutlined />
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item>
                  <Fab
                    size="small"
                    color="primary"
                    disabled={event.status === 'ARCHIVED'}
                    onClick={toggleOpenReservations}
                  >
                    <AddIcon />
                  </Fab>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
        <ReservationsTable
          eventId={event.id}
          reservations={reservations}
          pagination={{
            totalItems,
            page: options.page,
            limit: options.limit,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
        />
      </TabPanel>
      {user.role === 'organizer' && (
        <TabPanel disabled={isLoading} value={tabIndex} index={3}>
          <Grid item container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="h5">{t('Notifications')}</Typography>
            </Grid>
            <Grid item>
              <Fab size="small" color="primary" disabled={tickets.length <= 0} onClick={toggleOpenNotifications}>
                <AddIcon />
              </Fab>
            </Grid>
          </Grid>
          {notifications.length ? (
            <TableContainer sx={{ mt: 2 }} component={Paper}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">{t('Created')}</TableCell>
                    <TableCell align="left">{t('Channel')}</TableCell>
                    <TableCell align="left">{t('Subject')}</TableCell>
                    <TableCell align="left">{t('Body')}</TableCell>
                    <TableCell align="left">{t('Attachments')}</TableCell>
                    <TableCell align="left">{t('Message Count')}</TableCell>
                    <TableCell align="left">{t('Price')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications.map(
                    ({
                      id,
                      created_at,
                      channel,
                      subject,
                      body,
                      include_tickets_attachment,
                      scheduled_mesages,
                      price,
                    }) => (
                      <TableRow
                        key={id}
                        sx={{
                          textAlign: 'right',
                          '&:last-child td, &:last-child th': { borderBottom: 0 },
                          '&:nth-of-type(odd)': (theme) => ({
                            backgroundColor: theme.palette.action.hover,
                          }),
                        }}
                      >
                        <TableCell>{dayjs(created_at).format('DD/MM/YYYY HH:mm')}</TableCell>
                        <TableCell>{channel}</TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              maxWidth: 400,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {subject || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              maxWidth: 400,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {body}
                          </Typography>
                        </TableCell>
                        <TableCell>{include_tickets_attachment ? 'Yes' : 'No'}</TableCell>
                        <TableCell>{scheduled_mesages}</TableCell>
                        <TableCell>{price + ' Lei'}</TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <NoData />
          )}
        </TabPanel>
      )}
      {user.role === 'organizer' && (
        <TabPanel disabled={isLoading} value={tabIndex} index={4}>
          <Typography gutterBottom variant="h5">
            {t('Company')}
          </Typography>
          <TableContainer sx={{ marginTop: 2, marginBottom: 1 }} component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('Name')}</TableCell>
                  <TableCell>{t('Tax Code')}</TableCell>
                  <TableCell>{t('Price zero fiscalization')}</TableCell>
                  <TableCell>{t('Cashier Fixed Com.')}</TableCell>
                  <TableCell align="left">{t('Platform Comission') + '*'}</TableCell>
                  <TableCell align="left">{t('Seat Price') + '*'}</TableCell>
                  <TableCell align="left">{t('Free Seat Price') + '*'}</TableCell>
                  <TableCell align="left">{t('Cancel Seat Price') + '*'}</TableCell>
                  <TableCell align="left">{t('Sms Price') + '*'}</TableCell>
                  <TableCell align="left">{t('Email Price') + '*'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow
                  sx={{
                    textAlign: 'right',
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                    '&:nth-of-type(odd)': (theme) => ({
                      backgroundColor: theme.palette.action.hover,
                    }),
                  }}
                >
                  <TableCell>{event.company_name}</TableCell>
                  <TableCell>{event.tax_code}</TableCell>
                  <TableCell>{!!event.is_zero_price_fiscalized ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{!!event.comission_cash_per_seat ? 'Yes' : 'No'}</TableCell>
                  <TableCell align="left">{event.platform_comission * 100 + ' %'}</TableCell>
                  <TableCell align="left">{event.seat_price + ' Lei'}</TableCell>
                  <TableCell align="left">{event.free_seat_price + ' Lei'}</TableCell>
                  <TableCell align="left">{event.cancel_seat_price + ' Lei'}</TableCell>
                  <TableCell align="left">{event.notification_sms_price + ' Lei'}</TableCell>
                  <TableCell align="left">{event.notification_email_price + ' Lei'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography gutterBottom component="p" variant="caption">
            {t('vatDisclaimer')}
          </Typography>
          <Typography gutterBottom variant="h5">
            {t('DGITL')}
          </Typography>
          {fiscalSerials.length ? (
            <TableContainer sx={{ mt: 2 }} component={Paper}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">{t('Category')}</TableCell>
                    <TableCell align="left">{t('Price')}</TableCell>
                    <TableCell align="left">{t('Serials')}</TableCell>
                    <TableCell align="left">{t('Current Serial')}</TableCell>
                    <TableCell align="left">{t('Total count')}</TableCell>
                    <TableCell align="left">{t('Emitted')}</TableCell>
                    <TableCell align="left">{t('Canceled')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fiscalSerials.map(
                    ({
                      id,
                      type_label,
                      is_default_type,
                      name,
                      prefix,
                      price,
                      discount,
                      start,
                      end,
                      count,
                      sold,
                      canceled,
                    }) => (
                      <TableRow
                        key={id}
                        sx={{
                          textAlign: 'right',
                          '&:last-child td, &:last-child th': { borderBottom: 0 },
                          '&:nth-of-type(odd)': (theme) => ({
                            backgroundColor: theme.palette.action.hover,
                          }),
                        }}
                      >
                        <TableCell>
                          {name ? (
                            <>
                              {name}
                              {!is_default_type && <Chip sx={{ marginLeft: 1 }} size="small" label={type_label} />}
                            </>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>{price ? (price - price * discount).toFixed(2) + ' Lei' : '-'}</TableCell>
                        <TableCell>{`${prefix} ${start}-${end}`}</TableCell>
                        <TableCell>{sold ? `${prefix}-${start + sold - 1}` : '-'}</TableCell>
                        <TableCell>{count}</TableCell>
                        <TableCell>{sold}</TableCell>
                        <TableCell>{canceled}</TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <NoData />
          )}
        </TabPanel>
      )}
      <OrdersDialog eventId={event.id} open={openOrders} onClose={toggleOpenOrders} />
      <CategoryConfigDialog open={openCatConfig} onClose={toggleOpenCatConfig} event={event} />
      <ForSaleConfigDialog open={openForSaleConfig} onClose={toggleOpenForSaleConfig} event={event} />
      <NotificationsDialog event={event} open={openNotifications} onClose={toggleOpenNotifications} />
      <ReservationsDialog eventId={event.id} open={openReservations} onClose={toggleOpenReservations} />
      <TicketPdfSelectorDialog eventId={event.id} orderId={selectedOrdId} onClose={() => setSelectedOrdId(null)} />
      {user.role === 'organizer' && (
        <>
          <EventsDrawer type={EventsDrawerType.UPDATE} open={!!editEvent} event={editEvent} onClose={onDrawerClose} />
          <EventHistoryDialog eventId={eventId} open={openHistory} onClose={toggleHistory} />
        </>
      )}
    </div>
  );
}

export default EventDetails;
